import type { Template } from '@piccolohealth/pbs-common';
import { ChakraV3, FadeImage } from '@piccolohealth/ui';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../../hooks/useAppContext';
import { TemplateActionMenu } from './TemplateActionMenu';
import { TemplateStatusTag } from './TemplateStatusTag';
import { TemplateTypeTag } from './TemplateTypeTag';

interface Props {
  template: Template;
}

export const TemplateCard = (props: Props) => {
  const { template } = props;

  const navigate = useNavigate();
  const { organization } = useAppContext();

  const onClick = React.useCallback(() => {
    void navigate(`/organizations/${organization.id}/templates/${template.id}`);
  }, [navigate, organization, template]);

  return (
    <ChakraV3.Card.Root w='xs' h='sm'>
      <ChakraV3.Stack h='sm' flexDir='column' cursor='pointer' overflow='hidden' onClick={onClick}>
        <ChakraV3.Card.Header mb={0} flexShrink={1} py={4} px={4}>
          <ChakraV3.Stack h='full' gap={1}>
            <ChakraV3.Stack gap={2}>
              <ChakraV3.HStack gap={2} align='start'>
                <ChakraV3.Heading size='sm' lineClamp={1} flex='1'>
                  {template.name}
                </ChakraV3.Heading>
                <TemplateTypeTag type={template.type} flexShrink={0} />
              </ChakraV3.HStack>
              <ChakraV3.HStack>
                <TemplateStatusTag status={template.status} />
                {template.labels.map((label) => (
                  <ChakraV3.Tag key={label.id} size='sm' colorPalette={label.color}>
                    {label.name}
                  </ChakraV3.Tag>
                ))}
              </ChakraV3.HStack>
            </ChakraV3.Stack>
          </ChakraV3.Stack>
        </ChakraV3.Card.Header>
        <ChakraV3.Separator />
        <ChakraV3.Card.Body px={3} py={2} flexShrink={1}>
          <ChakraV3.Stack h='xs' overflow='hidden'>
            {template.thumbnailUrl ? (
              <FadeImage src={template.thumbnailUrl} />
            ) : (
              <ChakraV3.Stack p={2}>
                <ChakraV3.SkeletonCircle size='10' variant='pulse' mb={4} />
                <ChakraV3.SkeletonText noOfLines={16} height='2' />
              </ChakraV3.Stack>
            )}
          </ChakraV3.Stack>
        </ChakraV3.Card.Body>
      </ChakraV3.Stack>
      <ChakraV3.Card.Footer px={4} py={2} flexShrink={0}>
        <ChakraV3.HStack w='full'>
          <TemplateActionMenu template={template} />
        </ChakraV3.HStack>
      </ChakraV3.Card.Footer>
    </ChakraV3.Card.Root>
  );
};
